import classNames from 'clsx';
import { ReactComponent as IconArrow } from 'img/icons/dropArrow.svg';
import React, { type ReactElement, memo, useMemo } from 'react';
import { createPortfolioInFolder } from 'reactApp/appHelpers/featuresHelpers/features/createPortfolioInFolder';
import { printPhotoAlbum } from 'reactApp/appHelpers/featuresHelpers/features/printPhotoAlbum';
import { type IViewContext, useViewContext } from 'reactApp/hocs/withViewContext';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { PrintPhotoAlbumButton } from 'reactApp/ui//PrintPhotoAlbumButton/PrintPhotoAlbumButton';
import { Breadcrumb } from 'reactApp/ui/Breadcrumbs/Breadcrumb';
import { CreatePortfolioButton } from 'reactApp/ui/CreatePortfolioButton/CreatePortfolioButton';
import { noop } from 'reactApp/utils/helpers';

import styles from './Breadcrumbs.css';
import type { BreadcrumbItem } from './Breadcrumbs.types';
import { BreadcrumbsDropdown } from './BreadcrumbsDropdown';

const BREADCRUMBS_WITHOUT_DROPDOWN = 3;

const isDropdownListType = (item: BreadcrumbItem | DropdownList): item is DropdownList => 'list' in item;

interface DropdownList {
    list: BreadcrumbItem[];
}

interface Props {
    breadcrumbs: BreadcrumbItem[];
    onClick?: (e: React.MouseEvent, breadcrumb: BreadcrumbItem) => void;
    onShareClick?: (e: React.MouseEvent, breadcrumb: BreadcrumbItem) => void;
    onPublishClick?: (e: React.MouseEvent, breadcrumb: BreadcrumbItem) => void;
    mode?: 'viewer' | '';
    storage?: EStorageType;
    // Количество крошек, которые не будут скрываться в дропдаун
    breadcrumbsCount?: number;
    isFollowToSharedFolders?: boolean;
    goToSharedFolders?: () => void;
    asPageTitle?: boolean;
}

const createList = (breadcrumbs: BreadcrumbItem[], breadcrumbsCount: number): (BreadcrumbItem | DropdownList)[] => {
    const showDropdown = breadcrumbs.length > breadcrumbsCount;

    if (!showDropdown) {
        return breadcrumbs;
    }

    const breadCrumbsList = [...breadcrumbs];
    const list = breadCrumbsList.splice(1, breadcrumbs.length - breadcrumbsCount);
    const [firstBreadCrumb, ...restBreadcrumbs] = breadCrumbsList;
    return [firstBreadCrumb, { list }, ...restBreadcrumbs];
};

const BreadCrumbsList = memo(
    ({
        onClick = noop,
        onShareClick = noop,
        onPublishClick = noop,
        mode = '',
        storage,
        isFollowToSharedFolders,
        goToSharedFolders,
        asPageTitle = false,
        list,
        viewContext,
    }: Omit<Props, 'breadcrumbs' | 'breadcrumbsCount'> & {
        list: (BreadcrumbItem | DropdownList)[];
        viewContext: IViewContext;
    }) => {
        const { isInlineIntegration } = getStorage(storage as EStorageType);

        return (
            <>
                {list.map((item, index) => {
                    const isActive = index === list.length - 1;
                    const breadCrumbMode = mode === 'viewer' ? (index === 0 ? 'light' : 'bold') : '';

                    return (
                        <div
                            key={isDropdownListType(item) ? 'list' : item.id}
                            className={classNames(styles.item, {
                                [styles.item_vk_embedded]: viewContext.isVKEmbedded,
                                [styles.item_active]: isActive,
                                [styles.item_more]: isDropdownListType(item),
                                [styles.item_only]: list.length === 1,
                                [styles[`item_${breadCrumbMode}`]]: breadCrumbMode,
                                [styles.item_inlineIntegration]: isInlineIntegration,
                            })}
                        >
                            {isDropdownListType(item) ? (
                                <BreadcrumbsDropdown onClick={onClick} list={item.list} isInlineIntegration={isInlineIntegration} />
                            ) : (
                                <Breadcrumb
                                    breadcrumb={item}
                                    storage={storage}
                                    isActive={isActive}
                                    onClick={onClick}
                                    onPublishClick={onPublishClick}
                                    onShareClick={onShareClick}
                                    data-qa-name={item.text}
                                    isFollowToSharedFolders={isFollowToSharedFolders}
                                    goToSharedFolders={goToSharedFolders}
                                    isVKEmbedded={viewContext.isVKEmbedded}
                                    isInlineIntegration={isInlineIntegration}
                                    asPageTitle={asPageTitle}
                                />
                            )}
                            {!isActive && (
                                <div className={styles.arrow}>
                                    <IconArrow />
                                </div>
                            )}
                        </div>
                    );
                })}
                {(createPortfolioInFolder === 'b' || createPortfolioInFolder === 'c') && list.length > 1 && (
                    <CreatePortfolioButton className={styles.button} />
                )}
                {printPhotoAlbum === 'c' && list.length > 1 && <PrintPhotoAlbumButton className={styles.button} />}
            </>
        );
    }
);

BreadCrumbsList.displayName = 'BreadCrumbsList';

export const BreadCrumbs = memo(
    ({ breadcrumbs, storage, breadcrumbsCount = BREADCRUMBS_WITHOUT_DROPDOWN, ...rest }: Props): ReactElement => {
        const list = useMemo(() => createList(breadcrumbs, breadcrumbsCount), [breadcrumbs, breadcrumbsCount]);
        const viewContext = useViewContext();

        return (
            <div
                data-selectbox="false"
                className={classNames({
                    [styles.root]: true,
                    [styles.root_public]: storage === EStorageType.public,
                    [styles.root_vk_embedded]: viewContext.isVKEmbedded,
                })}
            >
                <BreadCrumbsList list={list} viewContext={viewContext} storage={storage} {...rest} />
            </div>
        );
    }
);
BreadCrumbs.displayName = 'Breadcrumbs';
